import { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, User, Headset } from "lucide-react";
import { api } from "@/lib/api";

const SESSION_KEY = "vx-assistant-session";

export function AssistantChat({ onHandoff }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm the Vinconex assistant. Ask me anything about QR stickers, inventory sync, CRM leads, Specialist Match or pricing — or leave a message below and a real person will follow up." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/public/assistant/chat", { session_id: sessionId || null, message: text });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: data.reply, handoff: data.handoff }]);
      if (data.handoff) onHandoff?.();
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "I couldn't reach the assistant just now — leave your details in the form and we'll reply by email.", handoff: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="assistant-chat" className="tracing-top rounded-2xl border border-[#1C2233] bg-[#0B0F18] flex flex-col h-[520px]">
      <div className="px-5 py-4 border-b border-[#1C2233] flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-[#0A8CF2]/15 border border-[#0A8CF2]/40 flex items-center justify-center"><Bot className="h-4 w-4 text-[#7FC3FF]" /></span>
        <div>
          <div className="font-heading font-bold text-sm">Ask Vinconex</div>
          <div className="text-[11px] text-slate-500">AI assistant · answers in seconds, hands off to a human when needed</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="assistant-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`assistant-msg-${m.role}`}>
            {m.role === "assistant" && <Bot className="h-4 w-4 text-[#7FC3FF] mt-1.5 shrink-0" />}
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0A8CF2] text-white rounded-br-sm" : "bg-[#12141C] border border-[#2A2E3D] text-slate-200 rounded-bl-sm"}`}>
              {m.text}
              {m.handoff && (
                <div className="mt-2 pt-2 border-t border-slate-700/60 text-[11px] text-[#7FC3FF] flex items-center gap-1.5" data-testid="assistant-handoff">
                  <Headset className="h-3 w-3" /> A Vinconex team member will follow up — use the form to leave your details.
                </div>
              )}
            </div>
            {m.role === "user" && <User className="h-4 w-4 text-slate-500 mt-1.5 shrink-0" />}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-[#1C2233] flex gap-2">
        <input
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. How do leads get into DriveCentric?"
          className="flex-1 h-11 rounded-lg bg-slate-950 border border-slate-800 focus:border-[#0A8CF2] px-3 text-sm text-slate-100 outline-none placeholder:text-slate-600"
        />
        <button type="submit" disabled={busy || !input.trim()} data-testid="assistant-send-btn" className="h-11 w-11 rounded-lg bg-[#0A8CF2] hover:bg-[#3AA3FF] disabled:opacity-50 flex items-center justify-center">
          <Send className="h-4 w-4 text-white" />
        </button>
      </form>
    </div>
  );
}
